import React, { useState, useEffect } from 'react';
import { User, Advisor, UserRole, CurriculumModule, BrandingSettings } from './types';
import LoginPage from './pages/LoginPage';
import { AdminDashboard } from './pages/AdminDashboard';
import AdvisorDashboard from './pages/AdvisorDashboard';
import EvaluationPage from './pages/EvaluationPage';
import { api } from './services/api';

// Helper function to darken/lighten a hex color
const adjustColor = (color: string, amount: number): string => {
    return '#' + color.replace(/^#/, '').replace(/../g, c => ('0'+Math.min(255, Math.max(0, parseInt(c, 16) + amount)).toString(16)).substr(-2));
};

const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
          }
        : null;
};


const App: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<User | Advisor | null>(null);
  const [activeEvaluation, setActiveEvaluation] = useState<{ type: 'module' | 'final', module?: CurriculumModule } | null>(null);
  const [branding, setBranding] = useState<BrandingSettings | null>(null);

  useEffect(() => {
      const loadBranding = async () => {
          const settings = await api.getBrandingSettings();
          if (settings) {
              setBranding(settings);
          }
      };
      loadBranding();
  }, []);

  const handleBrandingUpdate = async (settings: Omit<BrandingSettings, 'key'>) => {
    await api.saveBrandingSettings(settings);
    const updatedSettings = await api.getBrandingSettings();
    setBranding(updatedSettings || null);
  };

  useEffect(() => {
    const styleId = 'custom-branding-styles';
    let styleElement = document.getElementById(styleId);
    
    if (branding && branding.primaryColor) {
        if (!styleElement) {
            styleElement = document.createElement('style');
            styleElement.id = styleId;
            document.head.appendChild(styleElement);
        }
        
        const primaryColor = branding.primaryColor;
        const primaryHoverColor = adjustColor(primaryColor, -20);
        const primaryDarkerColor = adjustColor(primaryColor, -50);
        const primaryLighterColor = adjustColor(primaryColor, 50);
        const primaryRgb = hexToRgb(primaryColor);
        
        styleElement.innerHTML = `
          /* === Base Colors: Buttons, Links, Borders === */
          .bg-blue-600, .bg-blue-500, .bg-purple-600, .bg-green-600, .bg-yellow-500 { background-color: ${primaryColor} !important; }
          .hover\\:bg-blue-700:hover, .hover\\:bg-purple-700:hover, .hover\\:bg-green-700:hover, .hover\\:bg-blue-600:hover, .hover\\:bg-yellow-600:hover { background-color: ${primaryHoverColor} !important; }
          .text-blue-600, .text-blue-500, .text-blue-400, .text-purple-400, .text-purple-600, .text-green-500, .text-green-400, .text-yellow-500, .text-yellow-400 { color: ${primaryColor} !important; }
          .border-blue-500, .border-blue-600, .border-purple-400, .border-green-600 { border-color: ${primaryColor} !important; }
          
          /* === Focus/Ring Colors === */
          .ring-blue-500, .focus\\:ring-purple-500:focus { --tw-ring-color: ${primaryColor} !important; }
          .focus\\:ring-blue-500:focus { --tw-ring-color: ${primaryColor} !important; }
          .focus\\:border-blue-500:focus { border-color: ${primaryColor} !important; }
          
          /* === Colors with Opacity & Variants === */
          ${primaryRgb ? `
          .bg-purple-600\\/20 { background-color: rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.2) !important; }
          .border-blue-500\\/50 { border-color: rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.5) !important; }
          .border-blue-200 { border-color: rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.3) !important; }
          .shadow-blue-500\\/10 { box-shadow: 0 10px 15px -3px rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.1), 0 4px 6px -4px rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.1) !important; }
          .text-purple-100 { color: ${primaryLighterColor} !important; }
          .bg-green-100 { background-color: rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.1) !important; }
          .text-green-800, .text-yellow-700, .text-yellow-900, .text-green-700 { color: ${primaryDarkerColor} !important; }
          .bg-blue-900 { background-color: ${adjustColor(primaryColor, -40)} !important; }
          .to-purple-900 { --tw-gradient-to: ${primaryColor} !important; }
          .from-blue-900 { --tw-gradient-from: ${adjustColor(primaryColor, -40)} !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to); }
          ` : ''}
        `;
    } else if (styleElement) {
        styleElement.remove();
    }
  }, [branding]);

  const handleLogin = (user: User | Advisor) => {
    if (user.token) {
      localStorage.setItem('apexUniversityToken', user.token);
      localStorage.setItem('protegoToken', user.token);
    }
    localStorage.setItem('apexUniversityUser', JSON.stringify(user));
    localStorage.setItem('protegoUser', JSON.stringify(user));
    setCurrentUser(user);
  };

  const handleLogout = () => {
    localStorage.removeItem('apexUniversityToken');
    localStorage.removeItem('apexUniversityUser');
    localStorage.removeItem('protegoToken');
    localStorage.removeItem('protegoUser');
    setCurrentUser(null);
    setActiveEvaluation(null);
  };
  
  const handleStartEvaluation = (module: CurriculumModule) => {
    setActiveEvaluation({ type: 'module', module });
  };

  const handleStartFinalEvaluation = () => {
    setActiveEvaluation({ type: 'final' });
  };

  const refreshCurrentUser = async () => {
    if (currentUser && currentUser.role === UserRole.ADVISOR) {
      try {
        const updatedAdvisor = await api.getAdvisorById(currentUser.id);
        setCurrentUser(updatedAdvisor);
      } catch (error) {
        console.error("Failed to refresh user data:", error);
      }
    }
  };

  const handleExitEvaluation = async () => {
    await refreshCurrentUser();
    setActiveEvaluation(null);
  };

  if (!currentUser) {
    return <LoginPage onLogin={handleLogin} logoUrl={branding?.logoUrl} />;
  }

  if (currentUser.role === UserRole.ADMIN) {
    return <AdminDashboard user={currentUser} onLogout={handleLogout} branding={branding} onBrandingUpdate={handleBrandingUpdate} />;
  }

  if (currentUser.role === UserRole.ADVISOR) {
    if (activeEvaluation) {
        return <EvaluationPage
            isFinal={activeEvaluation.type === 'final'}
            module={activeEvaluation.module}
            user={currentUser as Advisor}
            onExit={handleExitEvaluation}
        />;
    }
    return <AdvisorDashboard
        user={currentUser as Advisor}
        onLogout={handleLogout}
        onStartEvaluation={handleStartEvaluation}
        onStartFinalEvaluation={handleStartFinalEvaluation}
        logoUrl={branding?.logoUrl}
        onRefreshUser={refreshCurrentUser}
    />;
  }

  return <div>Error: Invalid user role.</div>;
};

export default App;